import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import type { CollectionMall } from '@/features/catalog/productCollection/collectionMalls';

/**
 * R4-A ⑤ — 이벤트 가변 동의 약관 admin CRUD.
 * be-catalog `AdminEventConsentTermController` 계약(상품 동의약관 1:1 미러):
 *   GET    `/admin/v1/events/{eventId}/consent-terms?mall=<BCP-47>`  → { data: EventConsentTermView[] }
 *   POST   `/admin/v1/events/{eventId}/consent-terms?mall=<BCP-47>`  body EventConsentTermRequest → { data: view }
 *   PUT    `/admin/v1/events/{eventId}/consent-terms/{termId}`       body EventConsentTermRequest → { data: view } (mall 없음 — termId 가 전역)
 *   DELETE `/admin/v1/events/{eventId}/consent-terms/{termId}`
 *
 * 신규 이벤트는 고정 3종(개인정보/배송일정/미성년자)이 기본 시드되어 목록에 노출되며 편집/추가/삭제 가능.
 * 몰별 별도 관리(탭 전환=mall 변경). envelope { success, data }.
 */

/** be-catalog EventConsentTermView — termId 는 string 직렬화(SF 키잉 drift 방지). */
export interface EventConsentTermView {
  termId: string;
  title: string;
  /** 약관 본문 HTML(에디터). 비어 있을 수 있음(링크만 사용 시). */
  contentHtml: string | null;
  /** 외부 약관 링크(선택). */
  linkUrl: string | null;
  required: boolean;
  sortOrder: number;
}

/** be-catalog EventConsentTermRequest — 생성/수정 공용. title 필수(≤200), linkUrl ≤500. */
export interface EventConsentTermPayload {
  title: string;
  contentHtml: string | null;
  linkUrl: string | null;
  required: boolean;
  sortOrder: number;
}

const BASE = '/admin/v1/events';

const listPath = (eventId: string, mall: CollectionMall) =>
  `${BASE}/${eventId}/consent-terms?mall=${encodeURIComponent(mall)}`;
const termPath = (eventId: string, termId: string) =>
  `${BASE}/${eventId}/consent-terms/${termId}`;

export const eventConsentTermsQueryKey = (eventId: string, mall: CollectionMall) =>
  ['admin', 'event-consent-terms', eventId, mall] as const;

export const useEventConsentTermsQuery = (eventId: string, mall: CollectionMall) =>
  useQuery<EventConsentTermView[]>({
    queryKey: eventConsentTermsQueryKey(eventId, mall),
    queryFn: async () => {
      if (!eventId || eventId === 'new') return [];
      const res = await adminFetch(listPath(eventId, mall));
      if (!res.ok) throw new Error(`event consent-terms fetch failed: ${res.status}`);
      const env = (await res.json()) as { data?: EventConsentTermView[] | null };
      return Array.isArray(env.data) ? env.data : [];
    },
    enabled: !!eventId && eventId !== 'new',
  });

export const useEventConsentTermMutation = (eventId: string, mall: CollectionMall) => {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: eventConsentTermsQueryKey(eventId, mall) });

  // 생성 — mall 은 `?mall=` 쿼리로 귀속(컬렉션 필드와 동일 관례).
  const create = useMutation({
    mutationFn: async (body: EventConsentTermPayload): Promise<void> => {
      const p = listPath(eventId, mall);
      const res = await adminFetch(p, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKeyFor('POST', p, body) },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`event consent-term create failed: ${res.status}`);
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({ termId, body }: { termId: string; body: EventConsentTermPayload }): Promise<void> => {
      const p = termPath(eventId, termId);
      const res = await adminFetch(p, {
        method: 'PUT',
        headers: { 'Idempotency-Key': idempotencyKeyFor('PUT', p, body) },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`event consent-term update failed: ${res.status}`);
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (termId: string): Promise<void> => {
      const p = termPath(eventId, termId);
      const res = await adminFetch(p, {
        method: 'DELETE',
        headers: { 'Idempotency-Key': idempotencyKeyFor('DELETE', p, '') },
      });
      if (!res.ok) throw new Error(`event consent-term delete failed: ${res.status}`);
    },
    onSuccess: invalidate,
  });

  return { create, update, remove };
};
